import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import {
  X,
  Search,
  Receipt,
  Wallet,
  AlertTriangle,
  CheckCircle2,
  CreditCard,
  Link as LinkIcon,
  Split as SplitIcon,
} from 'lucide-react';
import { EnhancedDeal, Payment, QBExpense, TransactionSplit } from '../../types';
import { useCRM } from '../../utils/CRMContext';
import { useDebitCardRegistry } from '../../hooks/useCardExpenses';
import UserAvatar from '../ui/UserAvatar';
import { resolveCardholderForTransaction, type CardholderMatch } from '../../utils/cardExpenseMatch';
import {
  setPaymentDealLink,
  setTransactionSplits,
  setExpenseAsOverhead,
  QB_EXPENSE_OVERHEAD_ASSIGN,
} from '../../utils/qbTransactionLinks';
import { suggestedDealIdsForQbTransaction } from '../../utils/qbDealSuggest';
import {
  buildQBLinkDealOptions,
  encodeQBLinkTargetId,
  parseQBLinkTargetId,
  phaseLabel,
  splitToLinkTargetId,
  type QBLinkDealOption,
} from '../../utils/qbDealLinkOptions';
import { planTransactionSplitSave, round2 } from '../../utils/transactionSplits';
import { expenseIsUnmatchedForPl, qbSyncedPaymentIsUnmatched } from '../../utils/projectPL';
import { categorizeUnifiedExpense } from '../../utils/cardExpensesApi';
import { fetchExpenseLegend, type ExpenseLegendRow } from '../../utils/expenseLegend';
import { inferExpenseCategory } from '../../utils/inferExpenseCategory';
import QBDealPicker, { QBDealOption } from './QBDealPicker';
import TransactionSplitEditor from './TransactionSplitEditor';
import { subscribePurchaseRequisitions } from '../../utils/purchasingRequisitions';
import type { PurchaseRequisition } from '../../types/purchasing';
import {
  encodeCardPoPickerId,
  parseCardPoPickerId,
  purchaseOrderPickerOptions,
  rankPurchaseOrdersForExpense,
} from '../../utils/cardExpensePurchaseMatch';
import {
  linkQbExpenseToPurchaseOrder,
  toCardPoMatchRequisition,
  unlinkQbExpenseFromPurchaseOrder,
} from '../../utils/cardExpensePurchaseApply';

type QBDealOptionWithMeta = QBLinkDealOption;

interface QBTransactionsModalProps {
  open: boolean;
  qbPayments: (Payment & { id: string })[];
  qbExpenses: (QBExpense & { id: string })[];
  /** Deals used for suggestions and P&L context (won + active precon). */
  deals: EnhancedDeal[];
  formatCurrencyFull: (n: number) => string;
  onClose: () => void;
  /** Logged-in PM uid — recorded on the override for the audit trail. */
  uid?: string;
}

type Kind = 'payment' | 'expense';

interface UnifiedTxn {
  id: string;
  kind: Kind;
  date: string;             // YYYY-MM-DD or '' (sort key)
  displayDate: string;      // formatted for display
  vendorOrCustomer: string; // vendor (expense) or customer (payment)
  description: string;
  amount: number;
  matchedDealId: string | null;
  matchedDealTitle: string | null;
  /** True when the expense/payment is in the financeAgent unmatched queue. */
  unmatched: boolean;
  /** True when category === 'overhead' (QB expense pool). */
  isOverhead: boolean;
  /**
   * Nickname of the QB bank/credit-card account (DepositToAccountRef.name for
   * payments, AccountRef.name for purchases). Empty string when the source
   * record doesn't carry it — we never substitute the QBO PaymentType enum
   * here because "Cash" / "Check" / "CreditCard" are payment methods, not
   * bank-feed nicknames.
   */
  bankAccount: string;
  /** Optional payment-type fallback hint (e.g. "Cash", "Check") shown when bankAccount is empty. */
  paymentTypeHint: string;
  /** QB CustomerRef when present (expenses). */
  customerName?: string | null;
  lineItems?: QBExpense['lineItems'];
  /** Existing splits on the source doc, if any. */
  splits: TransactionSplit[];
  /** True when linked to a debit card (Sequence or registry) but not yet assigned to a project. */
  cardPending: boolean;
  /** True when a cardholder is resolved for this expense. */
  cardMatched: boolean;
  /** Company debit card owner when bank account matches registry. */
  cardholder: CardholderMatch | null;
  purchaseRequisitionId?: string | null;
  poNumber?: string | null;
  quickbooksId?: string | null;
  entityType?: QBExpense['entityType'];
  sequenceTransactionId?: string | null;
}

type FilterMode = 'all' | 'unmatched' | 'matched' | 'card' | 'payments' | 'expenses' | 'overhead' | 'split';

const TABLE_COL_COUNT = 7;

function paymentDate(p: Payment & { id: string }): { sort: string; display: string } {
  if (p.paidAt instanceof Date) {
    const iso = p.paidAt.toISOString().slice(0, 10);
    return { sort: iso, display: p.paidAt.toLocaleDateString() };
  }
  if (p.paymentDate) {
    const iso = String(p.paymentDate).slice(0, 10);
    const d = new Date(p.paymentDate);
    return { sort: iso, display: Number.isNaN(d.getTime()) ? p.paymentDate : d.toLocaleDateString() };
  }
  return { sort: '', display: '—' };
}

function expenseDate(e: QBExpense & { id: string }): { sort: string; display: string } {
  if (!e.txnDate) return { sort: '', display: '—' };
  const iso = String(e.txnDate).slice(0, 10);
  const d = new Date(e.txnDate);
  return { sort: iso, display: Number.isNaN(d.getTime()) ? e.txnDate : d.toLocaleDateString() };
}

const QBTransactionsModal: React.FC<QBTransactionsModalProps> = ({
  open,
  qbPayments,
  qbExpenses,
  deals,
  formatCurrencyFull,
  onClose,
  uid,
}) => {
  const { cards: debitCards } = useDebitCardRegistry();
  const { crmUsers, timberProjects } = useCRM();
  const userPhotoById = useMemo(
    () => Object.fromEntries(crmUsers.map(u => [u.uid, u.photoURL])),
    [crmUsers],
  );
  const [search, setSearch] = useState('');
  const [filter, setFilter] = useState<FilterMode>('unmatched');
  const [savingId, setSavingId] = useState<string | null>(null);
  const [legendRows, setLegendRows] = useState<ExpenseLegendRow[]>([]);
  const [purchaseOrders, setPurchaseOrders] = useState<PurchaseRequisition[]>([]);

  useEffect(() => {
    if (!open) return;
    return subscribePurchaseRequisitions({ userId: uid || '', viewAll: true }, setPurchaseOrders);
  }, [open, uid]);

  useEffect(() => {
    if (!open) return;
    let cancelled = false;
    fetchExpenseLegend()
      .then(rows => {
        if (!cancelled) setLegendRows(rows);
      })
      .catch(err => console.warn('[QBTransactionsModal] legend load failed:', err));
    return () => {
      cancelled = true;
    };
  }, [open]);

  // Active split editor — only one at a time. `editingSplitKey` is `${kind}-${id}`.
  const [editingSplitKey, setEditingSplitKey] = useState<string | null>(null);
  const [splitDraft, setSplitDraft] = useState<TransactionSplit[]>([]);
  const [splitErr, setSplitErr] = useState<string | null>(null);

  // Rows that just became filter-misses — kept visible for 2s with a fade so
  // the PM gets visual confirmation the link landed before the row drops out.
  const [leavingKeys, setLeavingKeys] = useState<Set<string>>(new Set());
  const leaveTimersRef = useRef<Record<string, ReturnType<typeof setTimeout>>>({});

  const startLeaveTimer = useCallback((key: string) => {
    setLeavingKeys(prev => {
      const next = new Set(prev);
      next.add(key);
      return next;
    });
    if (leaveTimersRef.current[key]) {
      clearTimeout(leaveTimersRef.current[key]);
    }
    leaveTimersRef.current[key] = setTimeout(() => {
      setLeavingKeys(prev => {
        const next = new Set(prev);
        next.delete(key);
        return next;
      });
      delete leaveTimersRef.current[key];
    }, 2000);
  }, []);

  // Clean up timers on unmount.
  useEffect(() => {
    const timers = leaveTimersRef.current;
    return () => {
      Object.values(timers).forEach(t => clearTimeout(t));
    };
  }, []);

  // Build a unified, sorted list of every QB transaction.
  const allTxns = useMemo<UnifiedTxn[]>(() => {
    const fromPayments: UnifiedTxn[] = qbPayments
      .filter(p => p.source !== 'manual')
      .map(p => {
        const d = paymentDate(p);
        const splits = (p.splits || []).filter(s => Number.isFinite(s.amount));
        return {
          id: p.id,
          kind: 'payment' as Kind,
          date: d.sort,
          displayDate: d.display,
          vendorOrCustomer: p.customerName || p.matchedDealTitle || 'Customer',
          description: p.description || 'QB payment',
          amount: Number(p.amount) || 0,
          matchedDealId: p.matchedDealId || null,
          matchedDealTitle: p.matchedDealTitle || null,
          unmatched: qbSyncedPaymentIsUnmatched(p),
          isOverhead: false,
          cardPending: false,
          cardMatched: false,
          bankAccount: (p.bankAccountName || '').trim(),
          paymentTypeHint: (p.paymentMethod || '').trim(),
          splits,
          cardholder: resolveCardholderForTransaction(
            {
              bankAccountName: p.bankAccountName,
              bankAccountId: p.bankAccountId,
            },
            debitCards,
          ),
        };
      });

    const fromExpenses: UnifiedTxn[] = qbExpenses.map(e => {
      const d = expenseDate(e);
      const splits = (e.splits || []).filter(s => Number.isFinite(s.amount));
      const cardholder = resolveCardholderForTransaction(
        {
          bankAccountName: e.bankAccountName,
          bankAccountId: e.bankAccountId,
          cardLastFour: e.cardLastFour,
          assignedUserId: e.assignedUserId,
          assignedUserName: e.assignedUserName,
          categorizationStatus: e.categorizationStatus,
          sequenceCardId: e.sequenceCardId,
        },
        debitCards,
      );
      const cardPending = e.categorizationStatus === 'pending';
      return {
        id: e.id,
        kind: 'expense' as Kind,
        date: d.sort,
        displayDate: d.display,
        vendorOrCustomer: e.vendorName || 'Vendor',
        description: [e.accountName, e.description].filter(Boolean).join(' · ') || 'QB expense',
        amount: Number(e.totalAmount) || 0,
        matchedDealId: e.matchedDealId || null,
        matchedDealTitle: e.matchedDealTitle || null,
        unmatched: expenseIsUnmatchedForPl({ ...e, splits }),
        isOverhead: e.category === 'overhead',
        cardPending,
        cardMatched: Boolean(cardholder),
        bankAccount: (e.bankAccountName || '').trim(),
        paymentTypeHint: (e.paymentType || '').trim(),
        customerName: e.customerName || null,
        lineItems: e.lineItems,
        splits,
        cardholder,
        purchaseRequisitionId: e.purchaseRequisitionId || null,
        poNumber: e.poNumber || null,
        quickbooksId: e.quickbooksId || null,
        entityType: e.entityType,
        sequenceTransactionId: e.sequenceTransactionId || null,
      };
    });

    return [...fromPayments, ...fromExpenses].sort((a, b) => (a.date < b.date ? 1 : -1));
  }, [qbPayments, qbExpenses, debitCards]);

  // Map link target id → friendly label, used by pickers and split summaries.
  const dealOptions = useMemo<QBDealOptionWithMeta[]>(
    () => buildQBLinkDealOptions(deals, timberProjects),
    [deals, timberProjects],
  );

  const poMatchRows = useMemo(
    () => purchaseOrders.map(toCardPoMatchRequisition),
    [purchaseOrders],
  );

  const poOptions = useMemo(
    () => purchaseOrderPickerOptions(poMatchRows),
    [poMatchRows],
  );

  const expenseDealOptions = useMemo<QBDealOption[]>(
    () => [
      ...dealOptions.map(d => ({ id: d.id, label: d.label, searchText: d.searchText })),
      ...poOptions,
    ],
    [dealOptions, poOptions],
  );

  const dealTitleById = useMemo(() => {
    const map: Record<string, string> = {};
    dealOptions.forEach(d => {
      map[d.id] = d.rawTitle + (d.projectPhase ? ` · ${phaseLabel(d.projectPhase)}` : '');
    });
    return map;
  }, [dealOptions]);

  const expenseSpecialOptions = useMemo<QBDealOption[]>(
    () => [{
      id: QB_EXPENSE_OVERHEAD_ASSIGN,
      label: 'Company overhead (shared pool)',
      searchText: 'overhead shared company pool',
    }],
    [],
  );

  const naturalMatch = useCallback(
    (t: UnifiedTxn, q: string) => {
      if (q) {
        const haystack = `${t.vendorOrCustomer} ${t.description} ${t.matchedDealTitle || ''} ${t.bankAccount} ${t.paymentTypeHint}`.toLowerCase();
        if (!haystack.includes(q)) return false;
      }
      switch (filter) {
        case 'unmatched':
          return t.unmatched;
        case 'card':
          return t.kind === 'expense' && t.cardPending;
        case 'matched':
          return !t.unmatched && !t.isOverhead && t.splits.length === 0;
        case 'payments':
          return t.kind === 'payment';
        case 'expenses':
          return t.kind === 'expense' && !t.isOverhead;
        case 'overhead':
          return t.isOverhead;
        case 'split':
          return t.splits.length > 0;
        default:
          return true;
      }
    },
    [filter],
  );

  // Rows displayed in the table = natural matches PLUS rows that just left the
  // filter and are still inside their 2s fade-out grace window.
  const filtered = useMemo(() => {
    const q = search.trim().toLowerCase();
    const out: { txn: UnifiedTxn; leaving: boolean }[] = [];
    for (const t of allTxns) {
      const natural = naturalMatch(t, q);
      const key = `${t.kind}-${t.id}`;
      const isLeaving = leavingKeys.has(key);
      if (natural) {
        out.push({ txn: t, leaving: false });
      } else if (isLeaving) {
        // Row no longer matches the filter, but we keep it for the fade.
        // Search query still applies — once a user types, the row vanishes.
        if (!q) out.push({ txn: t, leaving: true });
      }
    }
    return out;
  }, [allTxns, search, naturalMatch, leavingKeys]);

  const filterCounts = useMemo(
    () => ({
      all: allTxns.length,
      unmatched: allTxns.filter(t => t.unmatched).length,
      card: allTxns.filter(t => t.cardPending).length,
      matched: allTxns.filter(t => !t.unmatched && !t.isOverhead && !t.cardPending && t.splits.length === 0).length,
      payments: allTxns.filter(t => t.kind === 'payment').length,
      expenses: allTxns.filter(t => t.kind === 'expense' && !t.isOverhead).length,
      overhead: allTxns.filter(t => t.isOverhead).length,
      split: allTxns.filter(t => t.splits.length > 0).length,
    }),
    [allTxns],
  );

  const handleAssign = useCallback(
    async (txn: UnifiedTxn, linkTargetId: string) => {
      if (linkTargetId === QB_EXPENSE_OVERHEAD_ASSIGN) {
        setSavingId(txn.id);
        try {
          if (txn.kind === 'expense') {
            if (txn.purchaseRequisitionId) {
              await unlinkQbExpenseFromPurchaseOrder({
                expenseId: txn.id,
                purchaseRequisitionId: txn.purchaseRequisitionId,
              });
            }
            await categorizeUnifiedExpense({
              expenseId: txn.id,
              target: QB_EXPENSE_OVERHEAD_ASSIGN,
              plBucket: 'overhead',
              plBucketSource: 'user',
            });
          } else {
            await setExpenseAsOverhead(txn.id, { uid });
          }
          startLeaveTimer(`${txn.kind}-${txn.id}`);
        } catch (err) {
          console.error('Error reassigning transaction:', err);
        } finally {
          setSavingId(null);
        }
        return;
      }

      const poId = parseCardPoPickerId(linkTargetId);
      if (poId && txn.kind === 'expense') {
        const requisition = purchaseOrders.find(row => row.id === poId);
        if (!requisition) return;
        setSavingId(txn.id);
        try {
          await linkQbExpenseToPurchaseOrder({
            expense: {
              id: txn.id,
              entityType: txn.entityType,
              quickbooksId: txn.quickbooksId || undefined,
              sequenceTransactionId: txn.sequenceTransactionId || undefined,
              purchaseRequisitionId: txn.purchaseRequisitionId || undefined,
            },
            requisition,
          });
          startLeaveTimer(`${txn.kind}-${txn.id}`);
        } catch (err) {
          console.error('Error linking expense to PO:', err);
        } finally {
          setSavingId(null);
        }
        return;
      }

      const parsed = parseQBLinkTargetId(linkTargetId || null);
      const finalDealId = parsed.dealId;
      const dealTitle = finalDealId
        ? dealOptions.find(d => d.id === linkTargetId)?.rawTitle
        : undefined;
      setSavingId(txn.id);
      try {
        if (txn.kind === 'expense' && txn.purchaseRequisitionId && !poId) {
          await unlinkQbExpenseFromPurchaseOrder({
            expenseId: txn.id,
            purchaseRequisitionId: txn.purchaseRequisitionId,
          });
        }
        if (parsed.projectPhase === 'preconstruction' && finalDealId) {
          if (txn.kind === 'expense') {
            await categorizeUnifiedExpense({
              expenseId: txn.id,
              target: linkTargetId,
              linkTargetId,
              dealTitle: dealTitleById[linkTargetId] || dealTitle,
              projectPhase: 'preconstruction',
              plBucketSource: 'auto',
            });
          } else {
            await setTransactionSplits(
              txn.kind,
              txn.id,
              [{
                dealId: finalDealId,
                projectPhase: 'preconstruction',
                amount: round2(txn.amount),
                dealTitle: dealTitleById[linkTargetId] || dealTitle,
              }],
              { uid },
            );
          }
        } else if (txn.kind === 'expense') {
          const inferred = inferExpenseCategory(legendRows, {
            vendorName: txn.vendorOrCustomer,
            description: txn.description,
          });
          await categorizeUnifiedExpense({
            expenseId: txn.id,
            target: linkTargetId,
            linkTargetId,
            dealTitle,
            legendId: inferred.legendId,
            plBucket: inferred.plBucket,
            plBucketSource: inferred.confidence === 'high' ? 'auto' : 'user',
          });
        } else if (txn.kind === 'payment') {
          await setTransactionSplits(txn.kind, txn.id, null, { uid });
          await setPaymentDealLink(txn.id, finalDealId, { uid, dealTitle });
        }
        startLeaveTimer(`${txn.kind}-${txn.id}`);
      } catch (err) {
        console.error('Error reassigning transaction:', err);
      } finally {
        setSavingId(null);
      }
    },
    [dealOptions, dealTitleById, legendRows, uid, startLeaveTimer, purchaseOrders],
  );

  /* ── Split editor ─────────────────────────────────────────────────── */

  const openSplit = useCallback(
    (txn: UnifiedTxn) => {
      setSplitErr(null);
      const key = `${txn.kind}-${txn.id}`;
      setEditingSplitKey(key);
      // Seed with existing splits, or start with the matched deal at full
      // amount + an empty second slice so the user can immediately split.
      if (txn.splits.length > 0) {
        setSplitDraft(
          txn.splits.map(s => ({
            ...s,
            dealId: splitToLinkTargetId(s) || s.dealId,
          })),
        );
      } else {
        const seed: TransactionSplit[] = [];
        if (txn.matchedDealId) {
          seed.push({
            dealId: encodeQBLinkTargetId(txn.matchedDealId, 'contracted'),
            dealTitle: txn.matchedDealTitle || dealTitleById[txn.matchedDealId],
            amount: round2(txn.amount),
          });
        }
        seed.push({ dealId: null, amount: 0 });
        setSplitDraft(seed);
      }
    },
    [dealTitleById],
  );

  const cancelSplit = useCallback(() => {
    setEditingSplitKey(null);
    setSplitDraft([]);
    setSplitErr(null);
  }, []);

  const updateSplitSlice = useCallback((idx: number, patch: Partial<TransactionSplit>) => {
    setSplitDraft(prev => {
      const next = prev.slice();
      next[idx] = { ...next[idx], ...patch };
      // Keep the title snapshot in sync with dealId changes.
      if (patch.dealId !== undefined) {
        next[idx].dealTitle = patch.dealId ? undefined : undefined;
      }
      return next;
    });
  }, []);

  const removeSplitSlice = useCallback((idx: number) => {
    setSplitDraft(prev => prev.filter((_, i) => i !== idx));
  }, []);

  const addSplitSlice = useCallback(() => {
    setSplitDraft(prev => [...prev, { dealId: null, amount: 0 }]);
  }, []);

  const splitAllocated = useMemo(
    () => splitDraft.reduce((s, x) => s + (Number(x.amount) || 0), 0),
    [splitDraft],
  );

  const saveSplit = useCallback(
    async (txn: UnifiedTxn) => {
      const plan = planTransactionSplitSave(txn.amount, splitDraft, dealTitleById);
      if (plan.kind === 'error') {
        setSplitErr(plan.message);
        return;
      }
      try {
        setSavingId(txn.id);
        if (plan.kind === 'single') {
          if (txn.kind === 'expense') {
            const targetId =
              plan.projectPhase === 'preconstruction' && plan.dealId
                ? encodeQBLinkTargetId(plan.dealId, 'preconstruction')
                : plan.dealId;
            const inferred = inferExpenseCategory(legendRows, {
              vendorName: txn.vendorOrCustomer,
              description: txn.description,
            });
            await categorizeUnifiedExpense({
              expenseId: txn.id,
              target: targetId,
              linkTargetId: targetId,
              dealTitle: plan.dealTitle,
              projectPhase: plan.projectPhase,
              legendId: inferred.legendId,
              plBucket: inferred.plBucket,
              plBucketSource: 'auto',
            });
          } else if (plan.projectPhase === 'preconstruction' && plan.dealId) {
            await setTransactionSplits(txn.kind, txn.id, [{
              dealId: plan.dealId,
              projectPhase: 'preconstruction',
              amount: round2(txn.amount),
              dealTitle: plan.dealTitle,
            }], { uid });
          } else {
            await setTransactionSplits(txn.kind, txn.id, null, { uid });
            await setPaymentDealLink(txn.id, plan.dealId, {
              uid,
              dealTitle: plan.dealTitle,
            });
          }
        } else if (txn.kind === 'expense') {
          const inferred = inferExpenseCategory(legendRows, {
            vendorName: txn.vendorOrCustomer,
            description: txn.description,
          });
          await categorizeUnifiedExpense({
            expenseId: txn.id,
            splits: plan.splits,
            legendId: inferred.legendId,
            plBucket: inferred.plBucket,
            plBucketSource: 'auto',
          });
        } else {
          await setTransactionSplits(txn.kind, txn.id, plan.splits, { uid });
        }
        startLeaveTimer(`${txn.kind}-${txn.id}`);
        cancelSplit();
      } catch (err) {
        console.error('Error saving split:', err);
        setSplitErr(err instanceof Error ? err.message : 'Save failed.');
      } finally {
        setSavingId(null);
      }
    },
    [splitDraft, uid, dealTitleById, legendRows, cancelSplit, startLeaveTimer],
  );

  const clearSplit = useCallback(
    async (txn: UnifiedTxn) => {
      try {
        setSavingId(txn.id);
        await setTransactionSplits(txn.kind, txn.id, null, { uid });
        startLeaveTimer(`${txn.kind}-${txn.id}`);
        cancelSplit();
      } catch (err) {
        console.error('Error clearing split:', err);
      } finally {
        setSavingId(null);
      }
    },
    [uid, cancelSplit, startLeaveTimer],
  );

  // Reset editor when modal closes.
  useEffect(() => {
    if (!open) cancelSplit();
  }, [open, cancelSplit]);

  if (!open) return null;

  return (
    <div className="sa-ppl-modal-backdrop" onClick={onClose}>
      <div
        className="sa-ppl-modal qbtx-modal"
        onClick={e => e.stopPropagation()}
        role="dialog"
        aria-modal="true"
        aria-label="QuickBooks transactions"
      >
        <div className="sa-ppl-modal-head">
          <div>
            <h3>QuickBooks transactions</h3>
            <p className="sa-ppl-modal-sub">
              Every payment and expense pulled from QuickBooks. Reassign anything the auto-matcher
              got wrong, link unmatched transactions to a project, or split a transaction across
              projects — including preconstruction and under-contract work. A split does not need to
              use the full amount; leave a remainder unallocated when needed.
            </p>
          </div>
          <button type="button" className="sa-ppl-modal-close" onClick={onClose} aria-label="Close">
            <X size={18} />
          </button>
        </div>

        <div className="sa-ppl-modal-toolbar">
          <div className="sa-ppl-modal-search">
            <Search size={14} />
            <input
              type="text"
              placeholder="Search vendor, customer, description, or bank…"
              value={search}
              onChange={e => setSearch(e.target.value)}
            />
          </div>
          <div className="qbtx-filters" role="tablist" aria-label="Transaction filter">
            <QbtxFilter active={filter === 'unmatched'} count={filterCounts.unmatched} tone="warn" onClick={() => setFilter('unmatched')}>
              <AlertTriangle size={11} /> Unmatched
            </QbtxFilter>
            {filterCounts.card > 0 && (
              <QbtxFilter active={filter === 'card'} count={filterCounts.card} tone="muted" onClick={() => setFilter('card')}>
                <CreditCard size={11} /> Card matched
              </QbtxFilter>
            )}
            <QbtxFilter active={filter === 'matched'} count={filterCounts.matched} tone="ok" onClick={() => setFilter('matched')}>
              <CheckCircle2 size={11} /> Matched
            </QbtxFilter>
            <QbtxFilter active={filter === 'split'} count={filterCounts.split} tone="muted" onClick={() => setFilter('split')}>
              <SplitIcon size={11} /> Split
            </QbtxFilter>
            <QbtxFilter active={filter === 'payments'} count={filterCounts.payments} onClick={() => setFilter('payments')}>
              <Wallet size={11} /> Payments
            </QbtxFilter>
            <QbtxFilter active={filter === 'expenses'} count={filterCounts.expenses} onClick={() => setFilter('expenses')}>
              <Receipt size={11} /> Expenses
            </QbtxFilter>
            <QbtxFilter active={filter === 'overhead'} count={filterCounts.overhead} tone="muted" onClick={() => setFilter('overhead')}>
              Overhead pool
            </QbtxFilter>
            <QbtxFilter active={filter === 'all'} count={filterCounts.all} onClick={() => setFilter('all')}>
              All
            </QbtxFilter>
          </div>
        </div>

        <div className="sa-ppl-modal-body qbtx-body">
          {filtered.length === 0 ? (
            <div className="sa-ppl-empty">No transactions match this filter.</div>
          ) : (
            <div className="qbtx-table-wrap">
              <table className="qbtx-table">
                <thead>
                  <tr>
                    <th>Type</th>
                    <th>Vendor / Customer</th>
                    <th>Description</th>
                    <th>Bank account</th>
                    <th>Date</th>
                    <th className="sa-ppl-num">Amount</th>
                    <th>Linked deal</th>
                  </tr>
                </thead>
                <tbody>
                  {filtered.map(({ txn: t, leaving }) => {
                    const key = `${t.kind}-${t.id}`;
                    const isEditing = editingSplitKey === key;
                    const hasSplit = t.splits.length > 0;
                    const rowClass = [
                      t.unmatched ? 'unmatched' : '',
                      hasSplit ? 'has-split' : '',
                      leaving ? 'is-leaving' : '',
                    ]
                      .filter(Boolean)
                      .join(' ');
                    return (
                      <React.Fragment key={key}>
                        <tr className={rowClass}>
                          <td>
                            <span className={`qbtx-kind qbtx-kind-${t.kind}`}>
                              {t.kind === 'payment' ? <Wallet size={11} /> : <Receipt size={11} />}
                              {t.kind === 'payment' ? 'Payment' : t.isOverhead ? 'Overhead' : 'Expense'}
                            </span>
                          </td>
                          <td>
                            <div className="qbtx-primary">{t.vendorOrCustomer}</div>
                            {t.cardMatched && t.kind === 'expense' && (
                              <span
                                className={`qbtx-chip qbtx-chip-card${t.cardPending ? ' qbtx-chip-card-pending' : ''}`}
                                title={
                                  t.cardPending
                                    ? 'Linked to debit card — awaiting cardholder categorization'
                                    : 'Linked to debit card'
                                }
                              >
                                Card matched
                              </span>
                            )}
                          </td>
                          <td className="qbtx-desc">{t.description}</td>
                          <td>
                            <div className="qbtx-bank-cell">
                              {t.cardholder && (
                                <UserAvatar
                                  name={t.cardholder.userName}
                                  userId={t.cardholder.userId}
                                  photoURL={userPhotoById[t.cardholder.userId]}
                                  className={`qbtx-cardholder${t.cardholder.pendingCategorization ? ' qbtx-cardholder-pending' : ''}`}
                                  title={
                                    t.cardholder.pendingCategorization
                                      ? `${t.cardholder.userName} · card charge awaiting categorization`
                                      : t.cardholder.sequenceHolderName
                                        ? `Sequence: ${t.cardholder.sequenceHolderName} · Cardholder: ${t.cardholder.userName}`
                                        : `Cardholder: ${t.cardholder.userName}`
                                  }
                                />
                              )}
                              {t.bankAccount ? (
                                <span className="qbtx-bank" title={t.bankAccount}>{t.bankAccount}</span>
                              ) : t.paymentTypeHint ? (
                                <span
                                  className="qbtx-bank-empty"
                                  title={`Bank-feed nickname not captured on this record. QuickBooks payment type: ${t.paymentTypeHint}. Re-sync QuickBooks to backfill the bank name.`}
                                >
                                  — <span className="qbtx-bank-hint">({t.paymentTypeHint})</span>
                                </span>
                              ) : (
                                <span
                                  className="qbtx-bank-empty"
                                  title="Bank account isn't captured for this record. Re-sync QuickBooks to backfill."
                                >
                                  —
                                </span>
                              )}
                            </div>
                          </td>
                          <td>{t.displayDate}</td>
                          <td className="sa-ppl-num">{formatCurrencyFull(t.amount)}</td>
                          <td>
                            {hasSplit ? (
                              <div className="qbtx-link-cell">
                                <SplitSummary
                                  splits={t.splits}
                                  total={t.amount}
                                  formatCurrencyFull={formatCurrencyFull}
                                  dealTitleById={dealTitleById}
                                />
                                <button
                                  type="button"
                                  className="qbtx-mini-btn"
                                  onClick={() => (isEditing ? cancelSplit() : openSplit(t))}
                                  disabled={savingId === t.id}
                                >
                                  {isEditing ? 'Close' : 'Edit split'}
                                </button>
                              </div>
                            ) : (
                              <div className="qbtx-link-cell">
                                <QBDealPicker
                                  value={
                                    t.kind === 'expense' && t.purchaseRequisitionId
                                      ? encodeCardPoPickerId(t.purchaseRequisitionId)
                                      : t.isOverhead
                                        ? QB_EXPENSE_OVERHEAD_ASSIGN
                                        : (t.matchedDealId || '')
                                  }
                                  dealOptions={t.kind === 'expense' ? expenseDealOptions : dealOptions}
                                  specialOptions={t.kind === 'expense' ? expenseSpecialOptions : []}
                                  suggestedIds={
                                    t.kind === 'expense'
                                      ? [
                                          ...rankPurchaseOrdersForExpense(
                                            {
                                              id: t.id,
                                              vendorName: t.vendorOrCustomer,
                                              description: t.description,
                                              totalAmount: t.amount,
                                              txnDate: t.date,
                                              purchaseRequisitionId: t.purchaseRequisitionId,
                                            },
                                            poMatchRows,
                                          ),
                                          ...suggestedDealIdsForQbTransaction(deals, {
                                            vendorName: t.vendorOrCustomer,
                                            description: t.description,
                                            customerName: t.customerName || undefined,
                                            lineItems: t.lineItems,
                                          }),
                                        ]
                                      : suggestedDealIdsForQbTransaction(deals, {
                                          vendorName: t.vendorOrCustomer,
                                          description: t.description,
                                          customerName: t.vendorOrCustomer,
                                        })
                                  }
                                  emptyLabel="— Unmatched —"
                                  unmatched={t.unmatched}
                                  disabled={savingId === t.id || isEditing}
                                  onChange={dealId => handleAssign(t, dealId)}
                                  aria-label="Linked deal"
                                />
                                <button
                                  type="button"
                                  className="qbtx-mini-btn"
                                  onClick={() => (isEditing ? cancelSplit() : openSplit(t))}
                                  disabled={savingId === t.id}
                                  title="Split this transaction across multiple projects."
                                >
                                  <SplitIcon size={11} />
                                  Split
                                </button>
                                {savingId === t.id && (
                                  <span className="qbtx-saving">
                                    <LinkIcon size={11} /> Saving…
                                  </span>
                                )}
                              </div>
                            )}
                          </td>
                        </tr>
                        {isEditing && (
                          <tr className="qbtx-split-editor-row">
                            <td colSpan={TABLE_COL_COUNT}>
                              <TransactionSplitEditor
                                txnTotal={t.amount}
                                slices={splitDraft}
                                allocated={splitAllocated}
                                error={splitErr}
                                dealOptions={dealOptions}
                                formatCurrencyFull={formatCurrencyFull}
                                onUpdateSlice={updateSplitSlice}
                                onRemoveSlice={removeSplitSlice}
                                onAddSlice={addSplitSlice}
                                onSave={() => saveSplit(t)}
                                onClear={hasSplit ? () => clearSplit(t) : undefined}
                                onCancel={cancelSplit}
                                saving={savingId === t.id}
                              />
                            </td>
                          </tr>
                        )}
                      </React.Fragment>
                    );
                  })}
                </tbody>
              </table>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

interface QbtxFilterProps {
  active: boolean;
  count: number;
  tone?: 'warn' | 'ok' | 'muted';
  onClick: () => void;
  children: React.ReactNode;
}
const QbtxFilter: React.FC<QbtxFilterProps> = ({ active, count, tone, onClick, children }) => (
  <button
    type="button"
    role="tab"
    aria-selected={active}
    className={`qbtx-filter ${active ? 'active' : ''} ${tone ? `qbtx-filter-${tone}` : ''}`}
    onClick={onClick}
  >
    <span className="qbtx-filter-label">{children}</span>
    <span className="qbtx-filter-count">{count}</span>
  </button>
);

/* ── Split summary chip (shown on saved splits) ───────────────────── */

interface SplitSummaryProps {
  splits: TransactionSplit[];
  total: number;
  formatCurrencyFull: (n: number) => string;
  dealTitleById: Record<string, string>;
}
const SplitSummary: React.FC<SplitSummaryProps> = ({ splits, total, formatCurrencyFull, dealTitleById }) => {
  const lines = splits.map(s => {
    const linkId = splitToLinkTargetId(s);
    const phase = phaseLabel(s.projectPhase);
    const title =
      s.dealTitle
      || (linkId ? dealTitleById[linkId] : null)
      || (s.dealId ? dealTitleById[s.dealId] : null)
      || 'Unallocated';
    const label = phase && !title.includes(phase) ? `${title} (${phase})` : title;
    return `${label}: ${formatCurrencyFull(s.amount)}`;
  });
  return (
    <span className="qbtx-split-summary" title={lines.join('\n')}>
      <SplitIcon size={11} />
      Split · {splits.length} slice{splits.length === 1 ? '' : 's'} · {formatCurrencyFull(total)}
    </span>
  );
};

export default QBTransactionsModal;
